import {
  roleHas,
  type ClientDto,
  type DashboardDto,
  type ReportLinkDto,
  type ReportScheduleDto,
} from "@iagenix/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  BarChart3,
  CalendarClock,
  Download,
  FileSpreadsheet,
  FileText,
  Link2,
  Mail,
  MoreHorizontal,
  Play,
  Plus,
  RefreshCw,
  Sparkles,
  Trash2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { AiPanel } from "@/components/ai/ai-panel";
import { PERIOD_ALL, PeriodPicker, type PeriodValue } from "@/components/campaigns/period-picker";
import { ClientFilter } from "@/components/client-filter";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { PageHeader } from "@/components/page-header";
import { DashboardView } from "@/components/reports/dashboard-view";
import { CreateLinkDialog, ScheduleDialog } from "@/components/reports/dialogs";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { errorMessage } from "@/lib/api";
import { queries } from "@/lib/queries";
import { reportsApi, reportsKey } from "@/lib/reports-api";
import { formatDate, formatRelative } from "@/lib/utils";

interface RelatoriosSearch {
  aba?: "dashboard" | "links" | "agendamentos";
  cliente?: string;
}

export const Route = createFileRoute("/app/$workspaceId/relatorios")({
  validateSearch: (raw: Record<string, unknown>): RelatoriosSearch => {
    const out: RelatoriosSearch = {};
    if (raw.aba === "links" || raw.aba === "agendamentos" || raw.aba === "dashboard")
      out.aba = raw.aba;
    if (typeof raw.cliente === "string" && raw.cliente) out.cliente = raw.cliente;
    return out;
  },
  component: RelatoriosPage,
});

const DEFAULT_PERIOD: PeriodValue = { preset: "30d", range: { from: null, to: null } };
const TODAS = "__todas__";

export function periodToParams(period: PeriodValue): Record<string, string> {
  if (period.preset) return period.preset === "maximo" ? {} : { preset: period.preset };
  const p: Record<string, string> = {};
  if (period.range.from) p.from = period.range.from;
  if (period.range.to) p.to = period.range.to;
  return p;
}

/** Dashboard do workspace / de um cliente com seletores de período e de contas (reutilizado pelo Cliente final). */
export function DashboardPanel({
  workspaceId,
  clientId,
  onClientChange,
  canExport,
  showClientFilter = true,
}: {
  workspaceId: string;
  clientId: string | undefined;
  onClientChange: (clientId: string | undefined) => void;
  canExport: boolean;
  showClientFilter?: boolean;
}) {
  const queryClient = useQueryClient();
  const [period, setPeriod] = useState<PeriodValue>(DEFAULT_PERIOD);
  const [account, setAccount] = useState(TODAS);
  // Fase 8: "Perguntar à IA" com o contexto do período/cliente/conta selecionados.
  const workspace = useQuery(queries.workspace(workspaceId));
  const canAi = workspace.data ? roleHas(workspace.data.role, "ai:use") : false;
  const [aiOpen, setAiOpen] = useState(false);
  const params = useMemo(() => {
    const p = periodToParams(period);
    if (clientId) p.clientId = clientId;
    if (account !== TODAS) p.accounts = account;
    return p;
  }, [period, clientId, account]);
  const dashboard = useQuery(queries.dashboard(workspaceId, params));
  const sync = useMutation({
    mutationFn: (act: string) => reportsApi.syncInsights(workspaceId, act),
    onSuccess: async (r) => {
      await queryClient.invalidateQueries({ queryKey: reportsKey(workspaceId) });
      toast.success(
        r.debounced ? "Sincronização já em andamento." : "Sincronização de métricas iniciada.",
      );
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const download = useMutation({
    mutationFn: (kind: "pdf" | "csv" | "xlsx") =>
      kind === "pdf"
        ? reportsApi.downloadPdf(workspaceId, params)
        : reportsApi.downloadExport(workspaceId, { ...params, kind: "dashboard", format: kind }),
    onSuccess: (name) => toast.success(`Arquivo ${name} baixado.`),
    onError: (err) => toast.error(errorMessage(err)),
  });
  const accounts = dashboard.data?.accounts ?? [];
  const pending = dashboard.data?.sync.filter((s) => s.running) ?? [];
  const never = dashboard.data?.sync.filter((s) => !s.insightsSyncedAt) ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        {showClientFilter ? (
          <ClientFilter workspaceId={workspaceId} value={clientId} onChange={onClientChange} />
        ) : null}
        <Select value={account} onValueChange={setAccount}>
          <SelectTrigger className="sm:w-64" aria-label="Contas">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TODAS}>Todas as contas</SelectItem>
            {accounts.map((a) => (
              <SelectItem key={a.metaId} value={a.metaId}>
                {a.name} · {a.currency}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <PeriodPicker
          value={period}
          onChange={(v) => setPeriod(v.preset === "maximo" ? PERIOD_ALL : v)}
        />
        <div className="flex gap-2 sm:ml-auto">
          {canAi ? (
            <Button variant="outline" onClick={() => setAiOpen(true)}>
              <Sparkles />
              Perguntar à IA
            </Button>
          ) : null}
          {canExport ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" loading={download.isPending}>
                  <Download />
                  Exportar
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => download.mutate("pdf")}>
                  <FileText /> PDF com a marca da agência
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => download.mutate("csv")}>
                  <FileSpreadsheet /> CSV
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => download.mutate("xlsx")}>
                  <FileSpreadsheet /> XLSX
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
          {canExport && accounts.length > 0 ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" loading={sync.isPending || pending.length > 0}>
                  <RefreshCw />
                  Sincronizar métricas
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuLabel>Conta</DropdownMenuLabel>
                {accounts.map((a) => {
                  const s = dashboard.data?.sync.find((x) => x.adAccountMetaId === a.metaId);
                  return (
                    <DropdownMenuItem key={a.metaId} onSelect={() => sync.mutate(a.metaId)}>
                      <div className="min-w-0 flex-1">
                        <p className="truncate">{a.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {s?.running
                            ? "sincronizando…"
                            : s?.insightsSyncedAt
                              ? `${s.daysWithData} dia(s) · ${formatRelative(s.insightsSyncedAt)}${s.backfillDone ? "" : " · backfill em andamento"}`
                              : "nunca sincronizada"}
                        </p>
                      </div>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      </div>

      {dashboard.isPending ? (
        <LoadingState rows={6} />
      ) : dashboard.isError ? (
        <ErrorState error={dashboard.error} onRetry={() => void dashboard.refetch()} />
      ) : dashboard.data.accounts.length === 0 ? (
        <EmptyState
          icon={<BarChart3 className="size-8" aria-hidden />}
          title="Nenhuma conta de anúncio"
          description={
            clientId
              ? "Este cliente não tem contas de anúncio vinculadas."
              : "Conecte uma conta Meta e vincule contas aos clientes para ver os relatórios."
          }
        />
      ) : (
        <>
          <SyncNotice dashboard={dashboard.data} never={never.length} running={pending.length} />
          <DashboardView dashboard={dashboard.data} />
        </>
      )}
      {aiOpen ? (
        <AiPanel
          workspaceId={workspaceId}
          context={{
            ...periodToParams(period),
            ...(clientId ? { clientId } : {}),
            ...(account !== TODAS ? { accounts: [account] } : {}),
          }}
          subtitle="Contexto: o dashboard que você está vendo (período, cliente e contas)."
          onClose={() => setAiOpen(false)}
        />
      ) : null}
    </div>
  );
}

function SyncNotice({
  dashboard,
  never,
  running,
}: {
  dashboard: DashboardDto;
  never: number;
  running: number;
}) {
  const errors = dashboard.sync.filter((s) => s.lastError);
  return (
    <p className="text-xs text-muted-foreground">
      Período {dashboard.period.from.split("-").reverse().join("/")} a{" "}
      {dashboard.period.to.split("-").reverse().join("/")}
      {dashboard.period.timeZone ? ` (fuso ${dashboard.period.timeZone})` : ""} · comparado com{" "}
      {dashboard.previousPeriod.from.split("-").reverse().join("/")} a{" "}
      {dashboard.previousPeriod.to.split("-").reverse().join("/")}
      {running > 0 ? ` · ${running} conta(s) sincronizando métricas…` : ""}
      {never > 0 ? ` · ${never} conta(s) ainda sem métricas (use "Sincronizar métricas")` : ""}
      {errors.length > 0 ? (
        <span className="text-destructive"> · falhou: {errors[0]?.lastError}</span>
      ) : null}
    </p>
  );
}

function RelatoriosPage() {
  const { workspaceId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const workspace = useQuery(queries.workspace(workspaceId));
  const clients = useQuery(queries.clients(workspaceId));
  const role = workspace.data?.role;
  const canManage = role ? roleHas(role, "campaigns:write") : false;
  const tab = search.aba ?? "dashboard";
  const patch = (p: { aba?: RelatoriosSearch["aba"]; cliente?: string | undefined }) =>
    void navigate({
      search: (prev) => {
        const next: RelatoriosSearch = {};
        const aba = "aba" in p ? p.aba : prev.aba;
        const cliente = "cliente" in p ? p.cliente : prev.cliente;
        if (aba) next.aba = aba;
        if (cliente) next.cliente = cliente;
        return next;
      },
    });
  const setTab = (aba: RelatoriosSearch["aba"]) => patch({ aba });
  const setClient = (cliente: string | undefined) => patch({ cliente });

  if (workspace.isPending) return <LoadingState rows={4} />;
  if (workspace.isError)
    return <ErrorState error={workspace.error} onRetry={() => void workspace.refetch()} />;

  return (
    <>
      <PageHeader
        title="Relatórios"
        description="Dashboard do workspace e de cada cliente, relatório público com a marca da agência, PDF, agendamento por e-mail e exportação."
      />
      <div className="space-y-4">
        <Tabs
          value={tab}
          onValueChange={(v) => setTab(v === "links" || v === "agendamentos" ? v : "dashboard")}
        >
          <TabsList>
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="links">Links públicos</TabsTrigger>
            <TabsTrigger value="agendamentos">Agendamentos</TabsTrigger>
          </TabsList>
        </Tabs>
        {tab === "dashboard" ? (
          <DashboardPanel
            workspaceId={workspaceId}
            clientId={search.cliente}
            onClientChange={setClient}
            canExport={role ? roleHas(role, "reports:read") : false}
          />
        ) : tab === "links" ? (
          <LinksPanel
            workspaceId={workspaceId}
            clients={clients.data ?? []}
            canManage={canManage}
            initialClientId={search.cliente}
          />
        ) : (
          <SchedulesPanel
            workspaceId={workspaceId}
            clients={clients.data ?? []}
            canManage={canManage}
            initialClientId={search.cliente}
          />
        )}
      </div>
    </>
  );
}

function LinksPanel({
  workspaceId,
  clients,
  canManage,
  initialClientId,
}: {
  workspaceId: string;
  clients: ClientDto[];
  canManage: boolean;
  initialClientId: string | undefined;
}) {
  const queryClient = useQueryClient();
  const links = useQuery(queries.reportLinks(workspaceId));
  const [now] = useState(() => Date.now());
  const [open, setOpen] = useState(false);
  const [toRevoke, setToRevoke] = useState<ReportLinkDto | null>(null);
  const revoke = useMutation({
    mutationFn: (id: string) => reportsApi.revokeLink(workspaceId, id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: reportsKey(workspaceId) });
      setToRevoke(null);
      toast.success("Link revogado.");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-4">
        <div className="space-y-1">
          <CardTitle>Links públicos</CardTitle>
          <CardDescription>
            Cada link abre o dashboard de um cliente com a logo e a cor da agência. Senha e validade
            opcionais.
          </CardDescription>
        </div>
        {canManage ? (
          <Button onClick={() => setOpen(true)} disabled={clients.length === 0}>
            <Plus />
            Novo link
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        {links.isPending ? (
          <LoadingState rows={3} />
        ) : links.isError ? (
          <ErrorState error={links.error} onRetry={() => void links.refetch()} />
        ) : links.data.length === 0 ? (
          <EmptyState
            icon={<Link2 className="size-8" aria-hidden />}
            title="Nenhum link criado"
            description={
              clients.length === 0
                ? "Cadastre um cliente antes de criar links."
                : "Crie um link para compartilhar o relatório com o cliente."
            }
          />
        ) : (
          <ul className="divide-y">
            {links.data.map((l) => (
              <li key={l.id} className="flex flex-wrap items-center gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{l.clientName}</p>
                  <p className="text-xs text-muted-foreground">
                    criado {formatRelative(l.createdAt)}
                    {l.createdByName ? ` por ${l.createdByName}` : ""} · {l.views} visualização(ões)
                    {l.lastViewedAt ? ` · última ${formatRelative(l.lastViewedAt)}` : ""}
                  </p>
                </div>
                <div className="flex flex-wrap gap-1">
                  {l.hasPassword ? (
                    <Badge variant="secondary">com senha</Badge>
                  ) : (
                    <Badge variant="outline">sem senha</Badge>
                  )}
                  {l.revokedAt ? (
                    <Badge variant="destructive">revogado</Badge>
                  ) : l.expiresAt && new Date(l.expiresAt).getTime() < now ? (
                    <Badge variant="warning">expirado</Badge>
                  ) : l.expiresAt ? (
                    <Badge variant="success">até {formatDate(l.expiresAt).split(" ")[0]}</Badge>
                  ) : (
                    <Badge variant="success">não expira</Badge>
                  )}
                </div>
                {canManage && !l.revokedAt ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive"
                    onClick={() => setToRevoke(l)}
                  >
                    <Trash2 />
                    Revogar
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      {open ? (
        <CreateLinkDialog
          workspaceId={workspaceId}
          clients={clients}
          initialClientId={initialClientId}
          open
          onOpenChange={setOpen}
        />
      ) : null}
      <ConfirmDialog
        open={toRevoke !== null}
        onOpenChange={(o) => (o ? undefined : setToRevoke(null))}
        title="Revogar este link?"
        description="Quem tiver o endereço deixa de abrir o relatório na hora. Não dá para desfazer; crie outro link se precisar."
        confirmLabel="Revogar"
        destructive
        loading={revoke.isPending}
        onConfirm={() => (toRevoke ? revoke.mutate(toRevoke.id) : undefined)}
      />
    </Card>
  );
}

const WEEKDAY_SHORT = ["seg", "ter", "qua", "qui", "sex", "sáb", "dom"];

function SchedulesPanel({
  workspaceId,
  clients,
  canManage,
  initialClientId,
}: {
  workspaceId: string;
  clients: ClientDto[];
  canManage: boolean;
  initialClientId: string | undefined;
}) {
  const queryClient = useQueryClient();
  const schedules = useQuery(queries.reportSchedules(workspaceId));
  const [editing, setEditing] = useState<ReportScheduleDto | null | "novo">(null);
  const [toDelete, setToDelete] = useState<ReportScheduleDto | null>(null);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: reportsKey(workspaceId) });
  const run = useMutation({
    mutationFn: (id: string) => reportsApi.runSchedule(workspaceId, id),
    onSuccess: async (r) => {
      await invalidate();
      toast.success(
        `Relatório enviado para ${r.recipients.join(", ")} (em dev, o e-mail sai no console do wrangler).`,
      );
    },
    onError: async (err) => {
      await invalidate();
      toast.error(errorMessage(err));
    },
  });
  const toggle = useMutation({
    mutationFn: (s: ReportScheduleDto) =>
      reportsApi.updateSchedule(workspaceId, s.id, { active: !s.active }),
    onSuccess: async () => {
      await invalidate();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => reportsApi.deleteSchedule(workspaceId, id),
    onSuccess: async () => {
      await invalidate();
      setToDelete(null);
      toast.success("Agendamento excluído.");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-4">
        <div className="space-y-1">
          <CardTitle>Relatórios agendados</CardTitle>
          <CardDescription>
            Semanal ou mensal, por e-mail, com o PDF anexado ou um link seguro.
          </CardDescription>
        </div>
        {canManage ? (
          <Button onClick={() => setEditing("novo")} disabled={clients.length === 0}>
            <Plus />
            Agendar
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        {schedules.isPending ? (
          <LoadingState rows={3} />
        ) : schedules.isError ? (
          <ErrorState error={schedules.error} onRetry={() => void schedules.refetch()} />
        ) : schedules.data.length === 0 ? (
          <EmptyState
            icon={<CalendarClock className="size-8" aria-hidden />}
            title="Nenhum relatório agendado"
            description="Agende o envio semanal ou mensal para o cliente receber por e-mail."
          />
        ) : (
          <ul className="divide-y">
            {schedules.data.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium">
                    {s.clientName}{" "}
                    <span className="font-normal text-muted-foreground">
                      ·{" "}
                      {s.frequency === "semanal"
                        ? `toda ${WEEKDAY_SHORT[s.day - 1] ?? s.day}`
                        : `dia ${s.day}`}{" "}
                      às {String(s.hour).padStart(2, "0")}:00 ·{" "}
                      {s.delivery === "pdf" ? "PDF anexado" : "link seguro"}
                    </span>
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    <Mail className="mr-1 inline size-3" aria-hidden />
                    {s.recipients.join(", ")}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {s.active && s.nextRunAt
                      ? `próximo envio ${formatDate(s.nextRunAt)}`
                      : "pausado"}
                    {s.lastRunAt
                      ? ` · último ${formatRelative(s.lastRunAt)} (${s.lastRunStatus === "ok" ? "enviado" : `falhou: ${s.lastRunError ?? ""}`})`
                      : ""}
                  </p>
                </div>
                <Badge variant={s.active ? "success" : "secondary"}>
                  {s.active ? "ativo" : "pausado"}
                </Badge>
                {canManage ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" aria-label={`Ações de ${s.clientName}`}>
                        <MoreHorizontal />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onSelect={() => run.mutate(s.id)} disabled={run.isPending}>
                        <Play /> Disparar agora
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => setEditing(s)}>Editar</DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => toggle.mutate(s)}>
                        {s.active ? "Pausar" : "Reativar"}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive"
                        onSelect={() => setToDelete(s)}
                      >
                        <Trash2 /> Excluir
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      {editing !== null ? (
        <ScheduleDialog
          workspaceId={workspaceId}
          clients={clients}
          schedule={editing === "novo" ? null : editing}
          initialClientId={initialClientId}
          open
          onOpenChange={(o) => (o ? undefined : setEditing(null))}
        />
      ) : null}
      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(o) => (o ? undefined : setToDelete(null))}
        title="Excluir este agendamento?"
        description="Os envios futuros param. O histórico de envios já feitos some junto."
        confirmLabel="Excluir"
        destructive
        loading={remove.isPending}
        onConfirm={() => (toDelete ? remove.mutate(toDelete.id) : undefined)}
      />
    </Card>
  );
}
